/**
 * Hero Slider
 *
 * A fullscreen hero slider with:
 * - Video or image backgrounds
 * - Thumbnail navigation that syncs with slides
 * - Autoplay support
 */

class HeroSlider extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) * 1000 || 5000;

    this.mainSlider = null;
    this.pagination = this.dataset.pagination === 'true';
    this.thumbnailButtons = [];
    this.intersectionObserver = null;
    this.isVisible = true;
    this.isPlaying = true;
    this.playPauseButton = null;

    this.init();
  }

  init() {
    // Wait for Splide to be available
    if (typeof Splide === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.initSlider());
      return;
    }
    this.initSlider();
  }

  initSlider() {
    const mainEl = this.querySelector(`#hero-slider-main-${this.sectionId}`);

    if (!mainEl) {
      return;
    }

    // Initialize main slider with fade effect
    const options = {
      type: 'fade',
      rewind: true,
      pagination: this.pagination,
      arrows: false,
      drag: true,
      speed: 500,
      classes: {
        pagination: 'splide__pagination splide__dots',
        page: 'splide__pagination__page splide__dot',
      },
    };

    // Add autoplay if enabled
    if (this.autoplay) {
      options.autoplay = true;
      options.interval = this.autoplaySpeed;
      options.pauseOnHover = true;
      options.pauseOnFocus = true;
    }

    this.mainSlider = new Splide(mainEl, options);

    // Mount slider
    this.mainSlider.mount();

    // Setup thumbnail navigation
    this.setupThumbnails();

    // Setup play/pause button
    this.setupPlayPauseButton();

    // Handle slide change events
    this.mainSlider.on('move', (newIndex) => {
      this.updateThumbnails(newIndex);
      this.updatePlayPauseButton();
    });

    // Setup visibility observer for video pause/resume
    this.setupVisibilityObserver();
  }

  setupPlayPauseButton() {
    this.playPauseButton = this.querySelector('.hero-slider__play-pause');

    if (!this.playPauseButton) {
      return;
    }

    this.updatePlayPauseButton();

    // Handle click events
    this.playPauseButton.addEventListener('click', () => {
      this.togglePlayPause();
    });

    // Handle keyboard events for accessibility
    this.playPauseButton.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.togglePlayPause();
      }
    });
  }

  updatePlayPauseButton() {
    if (!this.playPauseButton) {
      return;
    }

    const currentSlideIndex = this.mainSlider.index;
    const currentSlide = this.querySelector(`#hero-slider-main-${this.sectionId} .splide__slide[data-index="${currentSlideIndex}"]`);
    const currentSlideForegroundColor = currentSlide.dataset.foregroundColor;
    const currentSlideBackgroundColor = currentSlide.dataset.backgroundColor;
    this.playPauseButton.style.setProperty('--foreground-color', currentSlideForegroundColor);
    this.playPauseButton.style.setProperty('--background-color', currentSlideBackgroundColor);
  }

  togglePlayPause() {
    this.isPlaying = !this.isPlaying;

    // Update button state and aria-label using localized strings from data attributes
    const pauseLabel = this.playPauseButton.dataset.labelPause || 'Pause slideshow';
    const playLabel = this.playPauseButton.dataset.labelPlay || 'Play slideshow';

    this.playPauseButton.dataset.playing = this.isPlaying.toString();
    this.playPauseButton.setAttribute(
      'aria-label',
      this.isPlaying ? pauseLabel : playLabel
    );

    if (this.isPlaying) {
      // Resume playback
      this.resumeVideos();
      if (this.autoplay && this.mainSlider) {
        this.mainSlider.Components.Autoplay?.play();
      }
    } else {
      // Pause playback
      this.pauseVideos();
      if (this.mainSlider) {
        this.mainSlider.Components.Autoplay?.pause();
      }
    }
  }

  setupVisibilityObserver() {
    // Use Intersection Observer to pause/resume videos based on visibility
    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.isVisible = true;
            // Only resume if the user hasn't manually paused
            if (this.isPlaying) {
              this.resumeVideos();
              if (this.autoplay && this.mainSlider) {
                this.mainSlider.Components.Autoplay?.play();
              }
            }
          } else {
            this.isVisible = false;
            this.pauseVideos();
            if (this.mainSlider) {
              this.mainSlider.Components.Autoplay?.pause();
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when at least 10% is visible
      }
    );

    this.intersectionObserver.observe(this);
  }

  pauseVideos() {
    // Pause all HTML5 videos
    const videos = this.querySelectorAll('video');
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    // Pause YouTube/Vimeo iframes by posting message
    const iframes = this.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      const src = iframe.src || '';
      if (src.includes('youtube.com')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
      }
    });
  }

  resumeVideos() {
    // Resume all HTML5 videos
    const videos = this.querySelectorAll('video');
    videos.forEach((video) => {
      if (video.paused) {
        video.play().catch(() => {
          // Ignore autoplay errors
        });
      }
    });

    // Resume YouTube/Vimeo iframes by posting message
    const iframes = this.querySelectorAll('iframe');
    iframes.forEach((iframe) => {
      const src = iframe.src || '';
      if (src.includes('youtube.com')) {
        iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      } else if (src.includes('vimeo.com')) {
        iframe.contentWindow?.postMessage('{"method":"play"}', '*');
      }
    });
  }

  setupThumbnails() {
    // Find all thumbnail buttons in the container
    this.thumbnailButtons = this.querySelectorAll('.hero-slider__thumbnail');

    this.thumbnailButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const slideIndex = parseInt(button.dataset.slideIndex, 10);
        if (!isNaN(slideIndex)) {
          this.mainSlider.go(slideIndex);
        }
      });
    });

    // Set initial active state
    this.updateThumbnails(0);
  }

  updateThumbnails(activeIndex) {
    // Update thumbnail active states across all slides
    // Each slide has its own set of thumbnails, so we need to update all of them
    const allThumbnails = this.querySelectorAll('.hero-slider__thumbnail');

    allThumbnails.forEach((thumbnail) => {
      const thumbIndex = parseInt(thumbnail.dataset.slideIndex, 10);
      if (thumbIndex === activeIndex) {
        thumbnail.classList.add('hero-slider__thumbnail--active');
      } else {
        thumbnail.classList.remove('hero-slider__thumbnail--active');
      }
    });
  }

  destroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.mainSlider) {
      this.mainSlider.destroy();
    }
  }
}

customElements.define('hero-slider', HeroSlider);

// // Initialize all hero sliders on the page
// function initHeroSliders() {
//   const sliders = document.querySelectorAll('.hero-slider');
//   sliders.forEach((slider) => {
//     if (!slider.dataset.initialized) {
//       new HeroSlider(slider);
//       slider.dataset.initialized = 'true';
//     }
//   });
// }

// // Initialize on DOM ready
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', initHeroSliders);
// } else {
//   initHeroSliders();
// }

// // Re-initialize for Shopify section rendering
// document.addEventListener('shopify:section:load', (event) => {
//   const section = event.target;
//   if (section.classList.contains('section--hero-slider')) {
//     const slider = section.querySelector('.hero-slider');
//     if (slider) {
//       slider.dataset.initialized = '';
//       new HeroSlider(slider);
//       slider.dataset.initialized = 'true';
//     }
//   }
// });
